import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { fmtEur } from '../../lib/constants';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { Percent, Euro, Tag, Pencil, RotateCcw } from 'lucide-react';

/**
 * Finestra rapida "Modifica articolo": consente di cambiare AL MOMENTO, e SOLO
 * per la riga da cui si è partiti:
 *  - il PREZZO unitario dell'articolo
 *  - uno SCONTO di riga (percentuale % oppure importo fisso in €)
 *  - la NOTA dell'articolo
 *
 * Props:
 *  - orderId: id dell'ordine
 *  - item: articolo del carrello (serve item.id)
 *  - open / onOpenChange: stato del dialog
 *  - onUpdated(order): callback con l'ordine aggiornato (total ricalcolato)
 */
export const ItemEditDialog = ({ orderId, item, open, onOpenChange, onUpdated }) => {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const [discType, setDiscType] = useState('percent'); // 'percent' | 'fixed'
  const [discValue, setDiscValue] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item && open) {
      setPrice(String(item.unit_price ?? ''));
      const ld = item.line_discount;
      setDiscType(ld?.type === 'fixed' ? 'fixed' : 'percent');
      setDiscValue(ld?.value ? String(ld.value) : '');
      setNotes(item.notes || '');
    }
  }, [item, open]);

  const preview = useMemo(() => {
    const qty = Number(item?.qty || 1);
    const extrasSum = (item?.extras || []).reduce((s, e) => s + Number(e.price || 0), 0);
    const unit = Math.max(0, Number(price || 0));
    const gross = (unit + extrasSum) * qty;
    let dval = Math.max(0, Number(discValue || 0));
    if (discType === 'percent' && dval > 100) dval = 100;
    let disc = dval > 0 ? (discType === 'percent' ? (gross * dval) / 100 : dval) : 0;
    disc = Math.min(disc, gross);
    return { qty, gross, disc, lineTotal: Math.max(0, gross - disc) };
  }, [item, price, discType, discValue]);

  if (!item) return null;

  const originalPrice = item.unit_price_original ?? item.unit_price;
  const priceChanged = Number(price || 0) !== Number(originalPrice || 0);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/orders/${orderId}/items/${item.id}`, {
        unit_price: Math.max(0, Number(price || 0)),
        line_discount: { type: discType, value: Math.max(0, Number(discValue || 0)) },
        notes: notes.trim(),
      });
      onUpdated?.(data);
      toast.success(t('itemEdit.saved', { defaultValue: 'Articolo aggiornato' }));
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error', { defaultValue: 'Errore' }));
    } finally { setBusy(false); }
  };

  const clearDiscount = () => setDiscValue('');
  const resetPrice = () => setPrice(String(originalPrice ?? ''));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noAnimation className="max-w-md" data-testid="item-edit-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            {t('itemEdit.title', { defaultValue: 'Modifica articolo' })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-xl bg-muted/50 px-3 py-2 text-sm font-medium" data-testid="item-edit-name">
            {item.name}{item.variant ? ` (${item.variant})` : ''} · ×{item.qty}
          </div>

          {/* Prezzo unitario */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="item-price">{t('itemEdit.price', { defaultValue: 'Prezzo unitario (€)' })}</Label>
              {priceChanged && (
                <button type="button" onClick={resetPrice} data-testid="item-edit-reset-price"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <RotateCcw className="h-3.5 w-3.5" /> {t('itemEdit.reset', { defaultValue: 'Ripristina' })} ({fmtEur(originalPrice)})
                </button>
              )}
            </div>
            <div className="relative">
              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input id="item-price" type="number" min="0" step="0.10" inputMode="decimal"
                value={price} onChange={(e) => setPrice(e.target.value)}
                className="h-12 pl-9 font-mono" data-testid="item-edit-price-input" />
            </div>
          </div>

          {/* Sconto di riga */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Tag className="h-4 w-4 text-primary" /> {t('itemEdit.discount', { defaultValue: 'Sconto (solo questo articolo)' })}</Label>
              {Number(discValue || 0) > 0 && (
                <button type="button" onClick={clearDiscount} data-testid="item-edit-clear-discount"
                  className="text-xs text-destructive hover:opacity-80 transition-opacity">
                  {t('itemEdit.removeDiscount', { defaultValue: 'Rimuovi sconto' })}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <div className="inline-flex rounded-xl border border-border p-0.5 shrink-0">
                <button type="button" onClick={() => setDiscType('percent')} data-testid="item-edit-disc-percent"
                  className={cn('h-11 w-11 rounded-lg flex items-center justify-center transition-colors', discType === 'percent' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                  <Percent className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setDiscType('fixed')} data-testid="item-edit-disc-fixed"
                  className={cn('h-11 w-11 rounded-lg flex items-center justify-center transition-colors', discType === 'fixed' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                  <Euro className="h-4 w-4" />
                </button>
              </div>
              <Input type="number" min="0" step={discType === 'percent' ? '1' : '0.10'} inputMode="decimal"
                value={discValue} onChange={(e) => setDiscValue(e.target.value)}
                placeholder={discType === 'percent' ? '0 %' : '0,00 €'}
                className="h-12 font-mono" data-testid="item-edit-disc-input" />
            </div>
          </div>

          {/* Nota */}
          <div className="space-y-1.5">
            <Label htmlFor="item-notes">{t('itemEdit.notes', { defaultValue: 'Nota' })}</Label>
            <Textarea id="item-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t('order.notePlaceholder', { defaultValue: 'es. senza ghiaccio' })}
              className="min-h-[64px]" data-testid="item-edit-notes-input" />
          </div>

          {/* Anteprima totale riga */}
          <div className="rounded-xl border border-border p-3 space-y-1" data-testid="item-edit-preview">
            {preview.disc > 0 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{t('payment.subtotal', { defaultValue: 'Subtotale' })}</span>
                <span className="tabular line-through">{fmtEur(preview.gross)}</span>
              </div>
            )}
            {preview.disc > 0 && (
              <div className="flex items-center justify-between text-sm text-primary">
                <span>{t('itemEdit.discountShort', { defaultValue: 'Sconto' })}</span>
                <span className="tabular">-{fmtEur(preview.disc)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="font-medium">{t('itemEdit.lineTotal', { defaultValue: 'Totale riga' })}</span>
              <span className="font-display text-xl font-semibold tabular" data-testid="item-edit-line-total">{fmtEur(preview.lineTotal)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl" data-testid="item-edit-cancel">
            {t('common.cancel', { defaultValue: 'Annulla' })}
          </Button>
          <Button onClick={save} disabled={busy} className="rounded-xl" data-testid="item-edit-save">
            {t('common.save', { defaultValue: 'Salva' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
